import { Footer } from "@/components/layout/Footer";
import { Navbar } from "@/components/layout/Navbar";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { useStripeCheckout } from "@/hooks/use-stripe-checkout";
import { supabase } from "@/integrations/supabase/client";
import { formatCurrency } from "@/lib/utils";
import { isProduction } from "@/utils/environment";
import {
	Elements,
	PaymentElement,
	useElements,
	useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import {
	ArrowLeft,
	ShoppingCart as CartIcon,
	MinusCircle,
	Package,
	PlusCircle,
	Trash,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

const stripePromise = loadStripe(
	isProduction()
		? "pk_live_51R3lAqGO7vUGr5nu7uAyHNL3uyyJVBwl31LOQIctLl4fjklyZfPXuot7veUqDE11cTZb0FTosGmi3PyRpKnMEz7100ool9cApj"
		: "pk_test_51R3lAqGO7vUGr5nuIbrg34ncYFrgc4hpLc3F7IDoJGeh9M0BzHXH5e7MFkhPVSqdOMbJKXfP3bBi4kkHCL1im68t00seiDMGCa"
);

interface SavedAddress {
	id: string;
	first_name: string;
	last_name: string;
	email: string;
	address: string;
	addressLine2: string;
	city: string;
	postal_code: string;
	state: string;
	phone: string;
	is_default: boolean;
}

interface SupplierInfo {
	id: string;
	name: string | null;
	business_name: string | null;
}

interface SupplierProfile {
	id: string;
	name: string | null;
	business_name: string | null;
	account_type: string;
	minimum_free_shipping: number | null;
	shipping_cost: number;
	minimum_order: number;
	shipping_cost_type: string;
}

const CheckoutForm = ({
	onSuccess,
}: {
	customerInfo: any;
	onSuccess: () => void;
	paymentIntentId: string;
}) => {
	const stripe = useStripe();
	const elements = useElements();
	const [isLoading, setIsLoading] = useState(false);
	const { toast } = useToast();
	const [isReady, setIsReady] = useState(false);

	const handleReady = () => {
		setIsReady(true);
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!stripe || !elements || !isReady) {
			return;
		}

		setIsLoading(true);

		const { error, paymentIntent } = await stripe.confirmPayment({
			elements,
			confirmParams: {
				return_url: `${window.location.origin}/checkout/success`,
			},
			redirect: "if_required",
		});

		if (error) {
			toast({
				title: "Payment Failed",
				description:
					error.message ||
					"An error occurred during payment. Please try again.",
				variant: "destructive",
			});
			setIsLoading(false);
		} else if (paymentIntent && paymentIntent.status === "succeeded") {
			toast({
				title: "Payment Successful",
				description: "Thank you for your purchase!",
			});
			onSuccess();
		}
	};

	return (
		<form onSubmit={handleSubmit} className="space-y-6">
			<PaymentElement onReady={handleReady} />
			<Button
				type="submit"
				className="w-full mt-4"
				size="lg"
				disabled={!stripe || !isReady || isLoading}>
				{isLoading ? "Processing..." : "Pay Now"}
			</Button>
		</form>
	);
};

const Checkout = () => {
	const { supplierId } = useParams();
	const { items, updateQuantity, removeFromCart, clearCart } = useCart();
	const { user, isLoading: authLoading } = useAuth();
	const { toast } = useToast();
	const [customerInfo, setCustomerInfo] = useState({
		firstName: "",
		lastName: "",
		email: "",
		address: "",
		addressLine2: "",
		city: "",
		postalCode: "",
		state: "",
		phone: "",
	});
	const [notes, setNotes] = useState("");
	const navigate = useNavigate();
	const { isLoading, clientSecret, createPaymentIntent } =
		useStripeCheckout();
	const [paymentComplete, setPaymentComplete] = useState(false);
	const [savedAddresses, setSavedAddresses] = useState<SavedAddress[]>([]);
	const [selectedAddressId, setSelectedAddressId] = useState<string | "new">(
		"new"
	);
	const [saveAddress, setSaveAddress] = useState(false);
	const [paymentIntentId, setPaymentIntentId] = useState<string>("");
	const [supplierProfile, setSupplierProfile] =
		useState<SupplierProfile | null>(null);
	const [shippingCost, setShippingCost] = useState(0);
	const [supplierTotal, setSupplierTotal] = useState(0);

	const supplierItems = items.filter(
		(item) => item.supplierId === supplierId
	);

	const isShippingFree = (allSamples: boolean) => {
		if (allSamples) return true;
		if (!supplierProfile?.minimum_free_shipping) return false;
		if (supplierProfile.minimum_free_shipping === 0) return false;
		const subtotal = supplierItems.reduce(
			(acc, item) => acc + item.price * item.quantity * item.caseQuantity,
			0
		);
		return subtotal >= supplierProfile.minimum_free_shipping;
	};

	const calculateShippingCost = (subtotal: number, allSamples: boolean) => {
		if (!supplierProfile) return 0;
		if (allSamples || isShippingFree(allSamples)) return 0;

		if (supplierProfile.shipping_cost_type === "percentage") {
			return (subtotal * supplierProfile.shipping_cost) / 100;
		}
		return supplierProfile.shipping_cost || 0;
	};

	useEffect(() => {
		const subtotal = supplierItems.reduce(
			(acc, item) => acc + item.price * item.quantity * item.caseQuantity,
			0
		);
		const allSamples = supplierItems.every((item) => item.isSample);
		const shipping = calculateShippingCost(subtotal, allSamples);

		setShippingCost(shipping);
		setSupplierTotal(subtotal + shipping);
	}, [supplierItems, supplierProfile]);

	const handleInputChange = (
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
	) => {
		const { name, value } = e.target;
		setCustomerInfo((prev) => ({ ...prev, [name]: value }));
	};

	const fetchSavedAddresses = async () => {
		if (!user) return;

		const { data, error } = await supabase
			.from("user_addresses")
			.select("*")
			.eq("user_id", user.id)
			.order("is_default", { ascending: false });

		if (error) {
			toast({
				title: "Error",
				description: "Failed to load saved addresses",
				variant: "destructive",
			});
			return;
		}

		setSavedAddresses(data);
		const defaultAddress = data.find((addr) => addr.is_default);
		if (defaultAddress) {
			setSelectedAddressId(defaultAddress.id);
			setCustomerInfo({
				firstName: defaultAddress.first_name,
				lastName: defaultAddress.last_name,
				email: defaultAddress.email,
				address: defaultAddress.address,
				addressLine2: defaultAddress.addressLine2,
				city: defaultAddress.city,
				postalCode: defaultAddress.postal_code,
				state: defaultAddress.state,
				phone: defaultAddress.phone,
			});
		}
	};

	const saveNewAddress = async () => {
		if (!user) return;

		const { error } = await supabase.from("user_addresses").insert({
			user_id: user.id,
			first_name: customerInfo.firstName,
			last_name: customerInfo.lastName,
			email: customerInfo.email,
			address: customerInfo.address,
			addressLine2: customerInfo.addressLine2,
			city: customerInfo.city,
			postal_code: customerInfo.postalCode,
			state: customerInfo.state,
			phone: customerInfo.phone,
			is_default: savedAddresses.length === 0,
		});

		if (error) {
			toast({
				title: "Error",
				description: "Failed to save address",
				variant: "destructive",
			});
			return false;
		}

		await fetchSavedAddresses();
		return true;
	};

	useEffect(() => {
		if (supplierItems.length === 0) {
			navigate("/products");
			toast({
				title: "No items found",
				description: "No items found for this supplier in your cart",
				variant: "destructive",
			});
		}
	}, [supplierItems]);

	const handleInitiateCheckout = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!user) {
			toast({
				title: "Authentication required",
				description: "You must be logged in to complete checkout",
				variant: "destructive",
			});
			navigate("/auth");
			return;
		}

		if (selectedAddressId === "new" && saveAddress) {
			const saved = await saveNewAddress();
			if (!saved) return;
		}

		const paymentSecret = await createPaymentIntent(
			supplierId!,
			customerInfo,
			supplierTotal,
			supplierItems.map((item) => ({
				name: item.name,
				id: item.id,
				price: item.price,
				quantity: item.quantity,
			}))
		);
		if (paymentSecret) {
			const match = paymentSecret.match(/^pi_(.+)_secret_(.+)$/);
			if (match) {
				setPaymentIntentId(`pi_${match[1]}`);
			}
		}
	};

	useEffect(() => {
		if (user) {
			fetchSavedAddresses();
		}
	}, [user]);

	const handleAddressSelect = (addressId: string) => {
		setSelectedAddressId(addressId);
		if (addressId === "new") {
			setCustomerInfo({
				firstName: "",
				lastName: "",
				email: "",
				address: "",
				addressLine2: "",
				city: "",
				postalCode: "",
				state: "",
				phone: "",
			});
		} else {
			const selectedAddress = savedAddresses.find(
				(addr) => addr.id === addressId
			);
			if (selectedAddress) {
				setCustomerInfo({
					firstName: selectedAddress.first_name,
					lastName: selectedAddress.last_name,
					email: selectedAddress.email,
					address: selectedAddress.address,
					addressLine2: selectedAddress.addressLine2,
					city: selectedAddress.city,
					postalCode: selectedAddress.postal_code,
					phone: selectedAddress.phone,
					state: selectedAddress.state,
				});
			}
		}
	};

	const handlePaymentSuccess = async () => {
		setPaymentComplete(true);

		const { data: orderData, error: orderFetchError } = await supabase
			.from("orders")
			.select("*")
			.eq("payment_intent_id", paymentIntentId)
			.single();

		if (!orderFetchError && orderData) {
			const { data: supplierData } = await supabase
				.from("profiles")
				.select("id, name, email, business_name")
				.eq("id", supplierId)
				.single();

			if (supplierData) {
				console.log(
					"Sending order notification to supplier:",
					supplierData.email
				);
				const supplierEmail =
					supplierData.email || "aniketdas001@gmail.com";

				await supabase.functions.invoke("send-order-notification", {
					body: {
						orderId: orderData.id,
						type: "new_order",
						recipientEmail: supplierEmail,
						recipientName:
							supplierData.name ||
							supplierData.business_name ||
							"Supplier",
						orderDetails: orderData,
					},
				});
			}
		}

		setTimeout(() => {
			clearCart();
			navigate("/dashboard/orders");
		}, 2000);
	};

	useEffect(() => {
		if (!authLoading && !user) {
			toast({
				title: "Authentication required",
				description: "You need to login to proceed with checkout",
			});
			navigate("/auth", { state: { from: "/checkout" } });
		}
	}, [user, authLoading, navigate, toast]);

	useEffect(() => {
		const fetchSupplierInfo = async () => {
			if (!supplierId) return;

			try {
				const { data, error } = await supabase
					.from("profiles")
					.select(
						"id, name, business_name, minimum_free_shipping, shipping_cost, minimum_order, account_type, shipping_cost_type"
					)
					.eq("id", supplierId)
					.single();

				if (error) throw error;
				setSupplierProfile({
					id: data.id,
					name: data.name,
					business_name: data.business_name,
					minimum_free_shipping: data.minimum_free_shipping,
					shipping_cost: data.shipping_cost,
					minimum_order: data.minimum_order,
					account_type: data.account_type || "supplier",
					shipping_cost_type: data.shipping_cost_type || "fixed",
				});
			} catch (error) {
				console.error("Error fetching supplier info:", error);
			}
		};

		fetchSupplierInfo();
	}, [supplierId]);

	if (supplierItems.length === 0 && !paymentComplete) {
		return (
			<div className="min-h-screen flex flex-col">
				<Navbar />
				<main className="flex-grow pt-20">
					<div className="container max-w-4xl mx-auto px-6 md:px-10 py-16">
						<div className="flex flex-col items-center justify-center text-center gap-4">
							<div className="h-20 w-20 rounded-full bg-gray-100 flex items-center justify-center">
								<CartIcon className="h-10 w-10 text-gray-400" />
							</div>
							<h1 className="text-2xl font-display font-semibold text-gray-900">
								Your cart is empty
							</h1>
							<p className="text-gray-600 max-w-md">
								There are no items in your cart. Add some
								products to proceed with checkout.
							</p>
							<Button
								className="mt-4"
								onClick={() => navigate("/products")}>
								Shop Now
							</Button>
						</div>
					</div>
				</main>
				<Footer />
			</div>
		);
	}

	if (paymentComplete) {
		return (
			<div className="min-h-screen flex flex-col">
				<Navbar />
				<main className="flex-grow pt-20">
					<div className="container max-w-4xl mx-auto px-6 md:px-10 py-16">
						<div className="flex flex-col items-center justify-center text-center gap-4">
							<div className="h-20 w-20 rounded-full bg-green-100 flex items-center justify-center">
								<svg
									xmlns="http://www.w3.org/2000/svg"
									className="h-10 w-10 text-green-600"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor">
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M5 13l4 4L19 7"
									/>
								</svg>
							</div>
							<h1 className="text-2xl font-display font-semibold text-gray-900">
								Payment Successful!
							</h1>
							<p className="text-gray-600 max-w-md">
								Thank you for your purchase. Your order has been
								received and is being processed. You will be
								redirected shortly
							</p>
						</div>
					</div>
				</main>
				<Footer />
			</div>
		);
	}

	return (
		<div className="min-h-screen flex flex-col">
			<Navbar />
			<main className="flex-grow pt-20">
				<div className="container max-w-6xl mx-auto px-6 md:px-10 py-10">
					<div className="flex items-center mb-8">
						<Button
							variant="ghost"
							className="text-gray-500 flex items-center gap-1"
							onClick={() => navigate("/products")}>
							<ArrowLeft className="h-4 w-4" />
							<span>Continue Shopping</span>
						</Button>
						<div className="flex-1 text-center">
							<h1 className="text-2xl md:text-3xl font-display font-semibold">
								Checkout from{" "}
								{supplierProfile &&
									(supplierProfile.business_name
										? supplierProfile.business_name
										: supplierProfile.name)}
							</h1>
						</div>
						<div className="w-24" />{" "}
					</div>

					{clientSecret ? (
						<div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
							<div className="lg:col-span-2 space-y-8">
								<div className="bg-white rounded-lg border p-6 space-y-4">
									<h2 className="text-lg font-medium">
										Complete Your Payment
									</h2>
									<Separator />

									<Elements
										stripe={stripePromise}
										options={{ clientSecret }}>
										<CheckoutForm
											customerInfo={customerInfo}
											onSuccess={handlePaymentSuccess}
											paymentIntentId={paymentIntentId}
										/>
									</Elements>
								</div>
							</div>

							<div>
								<div className="bg-white rounded-lg border p-6 sticky top-28">
									<h2 className="text-lg font-medium mb-4">
										Order Summary
									</h2>
									<Separator className="mb-4" />

									<div className="space-y-3">
										<div className="flex justify-between">
											<span className="text-gray-500">
												Subtotal
											</span>
											<span>
												{formatCurrency(
													supplierTotal - shippingCost
												)}
											</span>
										</div>
										<div className="flex justify-between">
											<span className="text-gray-500">
												Shipping
											</span>
											{shippingCost === 0 ? (
												<span className="text-green-600">
													Free
												</span>
											) : (
												<span>
													{formatCurrency(
														shippingCost
													)}
												</span>
											)}
										</div>
										<Separator />
										<div className="flex justify-between font-medium text-lg">
											<span>Total</span>
											<span>
												{formatCurrency(supplierTotal)}
											</span>
										</div>
									</div>

									<div className="mt-6">
										<div className="text-sm space-y-2">
											<h3 className="font-medium">
												Shipping To:
											</h3>
											<p>
												{customerInfo.firstName}{" "}
												{customerInfo.lastName}
												<br />
												{customerInfo.address}
												<br />
												{customerInfo.city},{" "}
												{customerInfo.postalCode}
												<br />
												{customerInfo.state}
											</p>
										</div>
									</div>
								</div>
							</div>
						</div>
					) : (
						<form onSubmit={handleInitiateCheckout}>
							<div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
								<div className="lg:col-span-2 space-y-8">
									<div className="bg-white rounded-lg border p-6 space-y-4">
										<h2 className="text-lg font-medium">
											Order Summary
										</h2>
										<Separator />

										<div className="space-y-6">
											{supplierItems.map((item) => (
												<div
													key={item.id}
													className="flex gap-4">
													<div className="h-24 w-24 rounded-md overflow-hidden bg-gray-50 flex-shrink-0">
														<img
															src={item.image}
															alt={item.name}
															className="h-full w-full object-cover"
														/>
													</div>
													<div className="flex-1 min-w-0">
														<h4 className="font-medium text-gray-900">
															{item.name}
														</h4>
														<p className="mt-1 text-sm text-gray-500">
															{formatCurrency(
																item.price
															)}{" "}
															×{" "}
															{item.quantity *
																item.caseQuantity}{" "}
															units
														</p>
														<p className="text-xs text-gray-500">
															<Package className="inline-block h-3 w-3 mr-1" />
															{item.quantity}{" "}
															cases (
															{item.caseQuantity}{" "}
															units per case)
														</p>
														<div className="mt-2 flex items-center gap-3">
															<Button
																type="button"
																variant="outline"
																size="icon"
																className="h-8 w-8"
																onClick={() =>
																	updateQuantity(
																		item.id,
																		item.quantity -
																			1
																	)
																}
																disabled={
																	item.quantity <=
																	1
																}>
																<MinusCircle className="h-4 w-4" />
															</Button>
															<span className="text-sm font-medium">
																{item.quantity}
															</span>
															<Button
																type="button"
																variant="outline"
																size="icon"
																className="h-8 w-8"
																onClick={() =>
																	updateQuantity(
																		item.id,
																		item.quantity +
																			1
																	)
																}>
																<PlusCircle className="h-4 w-4" />
															</Button>
														</div>
													</div>
													<div className="flex-shrink-0 flex flex-col items-end gap-2">
														<div className="font-medium">
															{formatCurrency(
																item.price *
																	item.quantity *
																	item.caseQuantity
															)}
														</div>
														<Button
															type="button"
															variant="ghost"
															size="icon"
															className="h-8 w-8 text-gray-400"
															onClick={() =>
																removeFromCart(
																	item.id
																)
															}>
															<Trash className="h-4 w-4" />
														</Button>
													</div>
												</div>
											))}
										</div>
									</div>

									<div className="bg-white rounded-lg border p-6 sticky top-28">
										<h2 className="text-lg font-medium mb-4">
											Shipping Information
										</h2>
										<Separator />

										{savedAddresses.length > 0 && (
											<div className="mb-6">
												<RadioGroup
													value={selectedAddressId}
													onValueChange={
														handleAddressSelect
													}
													className="space-y-4">
													{savedAddresses.map(
														(address) => (
															<div
																key={address.id}
																className="flex items-start space-x-3 bg-gray-50 p-4 rounded-md">
																<RadioGroupItem
																	value={
																		address.id
																	}
																	id={
																		address.id
																	}
																/>
																<Label
																	htmlFor={
																		address.id
																	}
																	className="flex-1 cursor-pointer">
																	<div className="font-medium">
																		{
																			address.first_name
																		}{" "}
																		{
																			address.last_name
																		}
																		{address.is_default && (
																			<span className="ml-2 text-sm text-gray-500">
																				(Default)
																			</span>
																		)}
																	</div>
																	<div className="text-sm text-gray-500 mt-1">
																		{
																			address.address
																		}
																		<br />
																		{
																			address.city
																		}
																		<br />
																		{
																			address.postal_code
																		}
																		<br />
																		{
																			address.state
																		}
																		<br />
																		{
																			address.phone
																		}
																	</div>
																</Label>
															</div>
														)
													)}
													<div className="flex items-start space-x-3 bg-gray-50 p-4 rounded-md">
														<RadioGroupItem
															value="new"
															id="new-address"
														/>
														<Label
															htmlFor="new-address"
															className="cursor-pointer">
															Add a new address
														</Label>
													</div>
												</RadioGroup>
											</div>
										)}

										{(selectedAddressId === "new" ||
											savedAddresses.length === 0) && (
											<>
												<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
													<div className="space-y-2">
														<Label htmlFor="firstName">
															First Name
														</Label>
														<Input
															id="firstName"
															name="firstName"
															value={
																customerInfo.firstName
															}
															onChange={
																handleInputChange
															}
															required
														/>
													</div>
													<div className="space-y-2">
														<Label htmlFor="lastName">
															Last Name
														</Label>
														<Input
															id="lastName"
															name="lastName"
															value={
																customerInfo.lastName
															}
															onChange={
																handleInputChange
															}
															required
														/>
													</div>
													<div className="space-y-2 md:col-span-2">
														<Label htmlFor="email">
															Email
														</Label>
														<Input
															id="email"
															name="email"
															type="email"
															value={
																customerInfo.email
															}
															onChange={
																handleInputChange
															}
															required
														/>
													</div>
													<div className="space-y-2 md:col-span-2">
														<Label htmlFor="address">
															Address
														</Label>
														<Input
															id="address"
															name="address"
															value={
																customerInfo.address
															}
															onChange={
																handleInputChange
															}
															required
														/>
													</div>
													<div className="space-y-2 md:col-span-2">
														<Label htmlFor="addressLine2">
															Address Line 2
															(Optional)
														</Label>
														<Input
															id="addressLine2"
															name="addressLine2"
															value={
																customerInfo.addressLine2
															}
															onChange={
																handleInputChange
															}
														/>
													</div>
													<div className="space-y-2">
														<Label htmlFor="city">
															City
														</Label>
														<Input
															id="city"
															name="city"
															value={
																customerInfo.city
															}
															onChange={
																handleInputChange
															}
															required
														/>
													</div>
													<div className="space-y-2">
														<Label htmlFor="postalCode">
															Postal Code
														</Label>
														<Input
															id="postalCode"
															name="postalCode"
															value={
																customerInfo.postalCode
															}
															onChange={
																handleInputChange
															}
															required
														/>
													</div>
													<div className="space-y-2 md:col-span-2">
														<Label htmlFor="state">
															State
														</Label>
														<Input
															id="state"
															name="state"
															value={
																customerInfo.state
															}
															onChange={
																handleInputChange
															}
															required
														/>
													</div>
													<div className="space-y-2 md:col-span-2">
														<Label htmlFor="phone">
															Phone Number
														</Label>
														<Input
															id="phone"
															name="phone"
															type="tel"
															value={
																customerInfo.phone
															}
															onChange={
																handleInputChange
															}
															required
														/>
													</div>
												</div>

												<div className="flex items-center space-x-2 mt-4">
													<Checkbox
														id="save-address"
														checked={saveAddress}
														onCheckedChange={(
															checked
														) =>
															setSaveAddress(
																checked as boolean
															)
														}
													/>
													<Label htmlFor="save-address">
														Save this address for
														future orders
													</Label>
												</div>
											</>
										)}
									</div>
								</div>

								<div>
									<div className="bg-white rounded-lg border p-6 sticky top-28">
										<h2 className="text-lg font-medium mb-4">
											Order Details
										</h2>
										<Separator className="mb-4" />

										<div className="space-y-3">
											<div className="flex justify-between">
												<span className="text-gray-500">
													Subtotal
												</span>
												<span>
													{formatCurrency(
														supplierTotal -
															shippingCost
													)}
												</span>
											</div>
											<div className="flex justify-between">
												<span className="text-gray-500">
													Shipping
												</span>
												{shippingCost === 0 ? (
													<span className="text-green-600">
														Free
													</span>
												) : (
													<span>
														{formatCurrency(
															shippingCost
														)}
													</span>
												)}
											</div>
											<Separator />
											<div className="flex justify-between font-medium text-lg">
												<span>Total</span>
												<span>
													{formatCurrency(
														supplierTotal
													)}
												</span>
											</div>
										</div>

										<div className="mt-6 space-y-4">
											<Button
												type="submit"
												className="w-full"
												size="lg"
												disabled={isLoading}>
												{isLoading
													? "Processing..."
													: "Proceed to Payment"}
											</Button>

											<div className="text-xs text-gray-500 text-center">
												By placing your order, you agree
												to our Terms of Service and
												Privacy Policy.
											</div>
										</div>

										<div className="mt-6">
											<Label
												htmlFor="notes"
												className="text-sm">
												Order Notes (Optional)
											</Label>
											<Textarea
												id="notes"
												name="notes"
												className="mt-1"
												placeholder="Special requests or instructions"
												value={notes}
												onChange={(e) =>
													setNotes(e.target.value)
												}
											/>
										</div>
									</div>
								</div>
							</div>
						</form>
					)}
				</div>
			</main>
			<Footer />
		</div>
	);
};

export default Checkout;
